'use client';

import React, {
  useState,
  useEffect,
  useMemo,
  Suspense,
  useRef,
  useCallback,
} from 'react';
import { TextGenerateEffect } from '@/components/features/chat/components/ui/text-generate-effect';
import { SourcesInChatResponse } from '@/components/features/chat/components/messages/SourcesInChatResponse';
import { FeedbackModal } from '@/components/features/chat/components/messages/FeedbackModal';
import { adviceActions, adviceState } from '@/lib/store/advice.store';
import { ConversationAdvice } from '@/types/api/conversation.types';
import { adviceService } from '@/lib/services';
import { useSession } from 'next-auth/react';
import { useScopedI18n } from '@/locales/client';
import { toast } from 'sonner';
import { ThumbsUp, ThumbsDown, Edit2, MessageSquare } from 'lucide-react';
import { MessageSkeleton } from './MessageSkeleton';

const REACTIONS = ['positive', 'negative'] as const;
type FeedbackReaction = (typeof REACTIONS)[number];

const isNegativeFeedback = (
  reaction: FeedbackReaction
): reaction is 'negative' => {
  return reaction === 'negative';
};

const isPositiveFeedback = (
  reaction: FeedbackReaction
): reaction is 'positive' => {
  return reaction === 'positive';
};

interface AIResponseProps {
  advice: ConversationAdvice;
}

export const AIResponse = React.memo(({ advice }: AIResponseProps) => {
  const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState<FeedbackReaction | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: session } = useSession();
  const t = useScopedI18n('actions');
  const tf = useScopedI18n('responseFeedback');
  const tc = useScopedI18n('chat');

  // Streaming detection variables - define these first before they're used
  const currentStreamingId = adviceState.streamingAdviceId.get();
  const isChatLoading = adviceState.isChatLoading.get();
  const isAdviceStreaming =
    Boolean(advice.isStreaming) === true ||
    Boolean((advice as any).streaming) === true;
  const isGenerating =
    advice.status === 'submitted' || advice.status === 'streaming';
  // Modify the isError declaration to more robustly detect error status
  const isError = useMemo(() => {
    return advice.status === 'error' || advice.isError === true;
  }, [advice.status, advice.isError]);
  const isCurrentStream =
    typeof advice.id === 'string' && advice.id === currentStreamingId;
  const storeStatus = adviceState.status.get();
  const storeIsStreaming = adviceState.isStreaming.get();

  // Determine if we're streaming - define this before using it anywhere else
  const isStreaming = useMemo(() => {
    const streaming =
      (isCurrentStream && storeStatus === 'streaming') ||
      isAdviceStreaming === true ||
      advice.status === 'streaming' ||
      (storeIsStreaming === true && isCurrentStream) ||
      Boolean(advice.isStreaming) === true;

    return streaming;
  }, [
    isCurrentStream,
    storeStatus,
    advice.status,
    isAdviceStreaming,
    advice.isStreaming,
    storeIsStreaming,
  ]);

  // Add a ref to track if this advice was already rendered via streaming
  const wasStreamedRef = useRef(false);

  // Update the wasStreamedRef when streaming is detected
  useEffect(() => {
    if (isStreaming) {
      wasStreamedRef.current = true;
    }
  }, [isStreaming]);

  // Generate the showSources state based on the presence of source content
  const [showSources, setShowSources] = useState(() => {
    // Pre-calculate if we have sources
    const hasUrls = Array.isArray(advice.urls) && advice.urls.length > 0;
    const hasUrlsCcq =
      Array.isArray(advice.urls_ccq) && advice.urls_ccq.length > 0;
    return hasUrls || hasUrlsCcq;
  });

  // Ensure hasSourceContent logic is working correctly
  const hasSourceContent = useMemo(() => {
    return (
      // Check for new sources array
      (Array.isArray(advice.sources) && advice.sources.length > 0) ||
      // Check legacy arrays
      (Array.isArray(advice.urls) && advice.urls.length > 0) ||
      (Array.isArray(advice.page_source) && advice.page_source.length > 0) ||
      (Array.isArray(advice.urls_ccq) && advice.urls_ccq.length > 0) ||
      (Array.isArray(advice.caselaw_reference) &&
        advice.caselaw_reference.length > 0)
    );
  }, [
    advice.sources,
    advice.urls,
    advice.page_source,
    advice.urls_ccq,
    advice.caselaw_reference,
  ]);

  // Initialize feedback status from advice
  useEffect(() => {
    if (advice.feedback?.status) {
      setFeedbackStatus(advice.feedback.status);
    }
  }, [advice.feedback]);

  const handleQuickFeedback = async (type: FeedbackReaction) => {
    if (!session) {
      toast.error('You must be logged in to submit feedback');
      return;
    }

    // If feedback exists, show edit modal
    if (advice.feedback) {
      setFeedbackStatus(type);
      setIsFeedbackVisible(true);
      return;
    }

    // For negative feedback, show modal
    if (isNegativeFeedback(type)) {
      setFeedbackStatus(type);
      setIsFeedbackVisible(true);
      return;
    }

    // For positive feedback, submit directly
    setIsSubmitting(true);
    try {
      await adviceService.submitFeedback(
        {
          email: session.user?.email || '',
          reaction: type,
          flaggedQuestion: advice.question || '',
          flaggedResponse: advice.advice || advice.response || '',
          comments: '',
        },
        session
      );

      // Update local state
      setFeedbackStatus(type);

      // Update advice state with new feedback
      const now = new Date().toISOString();
      const updatedAdvice = {
        ...advice,
        feedback: {
          status: type,
          details: {
            comments: '',
            createdAt: now,
            updatedAt: now,
          },
        },
      };
      adviceActions.updateAdvice(advice.id, updatedAdvice);

      toast.success(tf('success'));
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(tf('error'));
      setFeedbackStatus(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDetailedFeedback = () => {
    if (!session) {
      toast.error('You must be logged in to submit feedback');
      return;
    }
    setIsFeedbackVisible(true);
  };
  const hasAdviceContent =
    (typeof advice.advice === 'string' && advice.advice.trim().length > 0) ||
    (typeof advice.response === 'string' &&
      advice.response.trim().length > 0) ||
    (typeof advice.answer === 'string' && advice.answer.trim().length > 0);

  const responseText = advice.advice || advice.response || advice.answer || '';

  const isCompletedEmpty =
    advice.status === 'ready' && !hasAdviceContent && !hasSourceContent;

  const isLastAdvice =
    advice.isNew === true ||
    (typeof advice.id === 'string' && advice.id.includes('temp')) ||
    isCurrentStream ||
    isStreaming ||
    false;

  // Add forceUpdate helper
  const [, forceRender] = useState({});
  const forceUpdate = useCallback(() => forceRender({}), []);

  useEffect(() => {
    if (!hasSourceContent && advice) {
      // Check if the source content might be in the response as JSON
      try {
        if (
          typeof advice.advice === 'string' &&
          advice.advice.includes('"urls":')
        ) {
          const jsonStart = advice.advice.indexOf('", "urls":');
          if (jsonStart > 0) {
            const jsonPart = '{' + advice.advice.substring(jsonStart + 2);
            const metadataObj = JSON.parse(jsonPart);

            // Extract metadata from JSON and update the advice object
            if (metadataObj.urls && !advice.urls) {
              adviceActions.updateAdvice(advice.id, { urls: metadataObj.urls });
            }
            if (metadataObj.page_source && !advice.page_source) {
              adviceActions.updateAdvice(advice.id, {
                page_source: metadataObj.page_source,
              });
            }
            if (metadataObj.urls_ccq && !advice.urls_ccq) {
              adviceActions.updateAdvice(advice.id, {
                urls_ccq: metadataObj.urls_ccq,
              });
            }
            if (metadataObj.texts_ccq && !advice.texts_ccq) {
              adviceActions.updateAdvice(advice.id, {
                texts_ccq: metadataObj.texts_ccq,
              });
            }

            // Force render after updating
            setTimeout(forceUpdate, 50);
          }
        }
      } catch (e) {
        console.error('Error extracting metadata from advice:', e);
      }
    }
  }, [advice, hasSourceContent, forceUpdate]);

  useEffect(() => {
    // Emergency metadata extraction from response text
    if (!hasSourceContent && typeof advice.advice === 'string') {
      try {
        // Try to reconstruct the metadata as JSON from the text
        const rawText = advice.advice;

        // Pattern match urls array
        const urlsMatch = rawText.match(/"urls":\s*(\[[^\]]*\])/);
        if (urlsMatch && urlsMatch[1]) {
          try {
            const urls = JSON.parse(urlsMatch[1]);
            if (Array.isArray(urls) && urls.length > 0) {
              adviceActions.updateAdvice(advice.id, { urls });
            }
          } catch (e) {
            console.error('Failed to parse urls JSON:', e);
          }
        }

        // Pattern match urls_ccq array
        const urlsCcqMatch = rawText.match(/"urls_ccq":\s*(\[[^\]]*\])/);
        if (urlsCcqMatch && urlsCcqMatch[1]) {
          try {
            const urls_ccq = JSON.parse(urlsCcqMatch[1]);
            if (Array.isArray(urls_ccq) && urls_ccq.length > 0) {
              adviceActions.updateAdvice(advice.id, { urls_ccq });
            }
          } catch (e) {
            console.error('Failed to parse urls_ccq JSON:', e);
          }
        }

        // Pattern match texts_ccq array if needed
        const textsCcqMatch = rawText.match(/"texts_ccq":\s*(\[[^\]]*\])/);
        if (textsCcqMatch && textsCcqMatch[1]) {
          try {
            const texts_ccq = JSON.parse(textsCcqMatch[1]);
            if (Array.isArray(texts_ccq) && texts_ccq.length > 0) {
              adviceActions.updateAdvice(advice.id, { texts_ccq });
            }
          } catch (e) {
            console.error('Failed to parse texts_ccq JSON:', e);
          }
        }

        // Force update after metadata extraction
        setTimeout(forceUpdate, 100);
      } catch (e) {
        console.error('Emergency metadata extraction failed:', e);
      }
    }
  }, [advice.advice, hasSourceContent, advice.id, forceUpdate]);

  useEffect(() => {
    // Check if we have sources but they're not being displayed
    const hasUrls = Array.isArray(advice.urls) && advice.urls.length > 0;
    const hasUrlsCcq =
      Array.isArray(advice.urls_ccq) && advice.urls_ccq.length > 0;

    // Force source visibility if we have sources but they're not showing
    if ((hasUrls || hasUrlsCcq) && !showSources) {
      setShowSources(true);
    }
  }, [advice.urls, advice.urls_ccq, showSources]);

  // Check for and extract metadata from nested responses
  useEffect(() => {
    if (advice && typeof advice === 'object') {
      // Only proceed if we have advice and it's not during streaming
      if (advice.advice && !advice.isStreaming) {
        try {
          const hasNestedResponse =
            advice.response && typeof advice.response === 'object';

          if (hasNestedResponse) {
            // Check for missing values that might be in the nested response
            // First convert to unknown, then to Record<string, any> to satisfy TypeScript
            const responseObj = advice.response as unknown as Record<
              string,
              any
            >;

            // Create a local copy to update
            const updatedAdvice = { ...advice };
            let hasUpdates = false;

            // Add logic to copy page_source and page_title from the nested response if empty in main object
            if (
              Array.isArray(responseObj.urls) &&
              responseObj.urls.length > 0
            ) {
              // Check if regular page_source is empty but exists in nested response
              if (
                (!Array.isArray(updatedAdvice.page_source) ||
                  updatedAdvice.page_source.length === 0) &&
                Array.isArray(responseObj.page_source) &&
                responseObj.page_source.length > 0
              ) {
                updatedAdvice.page_source = responseObj.page_source;
                hasUpdates = true;
              }

              // Check if regular page_title is empty but exists in nested response
              if (
                (!Array.isArray(updatedAdvice.page_title) ||
                  updatedAdvice.page_title.length === 0) &&
                Array.isArray(responseObj.page_title) &&
                responseObj.page_title.length > 0
              ) {
                updatedAdvice.page_title = responseObj.page_title;
                hasUpdates = true;
              }
            }

            // If we don't have page_source but have page_source_ccq, copy it over as a fallback
            if (
              (!Array.isArray(updatedAdvice.page_source) ||
                updatedAdvice.page_source.length === 0) &&
              Array.isArray(updatedAdvice.page_source_ccq) &&
              updatedAdvice.page_source_ccq.length > 0 &&
              Array.isArray(updatedAdvice.urls) &&
              updatedAdvice.urls.length > 0 &&
              updatedAdvice.urls.length === updatedAdvice.page_source_ccq.length
            ) {
              updatedAdvice.page_source = [...updatedAdvice.page_source_ccq];
              hasUpdates = true;
            }

            // If we don't have page_title but have page_title_ccq, copy it over as a fallback
            if (
              (!Array.isArray(updatedAdvice.page_title) ||
                updatedAdvice.page_title.length === 0) &&
              Array.isArray(updatedAdvice.page_title_ccq) &&
              updatedAdvice.page_title_ccq.length > 0 &&
              Array.isArray(updatedAdvice.urls) &&
              updatedAdvice.urls.length > 0 &&
              updatedAdvice.urls.length === updatedAdvice.page_title_ccq.length
            ) {
              updatedAdvice.page_title = [...updatedAdvice.page_title_ccq];
              hasUpdates = true;
            }

            // If we have updates, update the props
            if (hasUpdates && session?.user?.email) {
              adviceService.updateAdvice(advice.id, updatedAdvice, session);
            }
          }
        } catch (error) {
          console.error(
            'Error extracting metadata from nested response',
            error
          );
        }
      }
    }
  }, [advice, session]);

  // Check for formatted content in session storage
  useEffect(() => {
    if (hasAdviceContent && !isStreaming && responseText) {
      try {
        // Check multiple possible storage key formats for backwards compatibility
        const storageKeys = [
          `md-${responseText.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}`,
          `markdown-content-${responseText.substring(0, 30)}`,
        ];

        let bestSavedContent = null;

        for (const key of storageKeys) {
          const savedContent = sessionStorage.getItem(key);

          if (
            savedContent &&
            savedContent.length > responseText.length * 0.8 &&
            (savedContent.includes('**') ||
              savedContent.includes('#') ||
              savedContent.includes('\n'))
          ) {
            // Use the longest content with most markdown indicators
            if (
              !bestSavedContent ||
              savedContent.length > bestSavedContent.length ||
              (savedContent.match(/\*\*|\#|\n/g)?.length || 0) >
                (bestSavedContent.match(/\*\*|\#|\n/g)?.length || 0)
            ) {
              bestSavedContent = savedContent;
            }
          }
        }

        // Only update if the current content doesn't have markdown
        const currentHasMarkdown =
          responseText.includes('**') ||
          responseText.includes('#') ||
          responseText.includes('\n');

        if (bestSavedContent && !currentHasMarkdown) {
          // Update the advice with the formatted content
          const updatedAdvice = { ...advice, advice: bestSavedContent };
          adviceActions.updateAdvice(advice.id, updatedAdvice);
        }
      } catch (e) {
        console.error('Error accessing session storage:', e);
      }
    }
  }, [hasAdviceContent, isStreaming, responseText, advice]);

  // Transform advice with sources array into flat arrays for UI rendering
  const processedAdvice = useMemo(() => {
    // If no sources array, just return the original advice
    if (!Array.isArray(advice?.sources) || advice.sources.length === 0) {
      return advice;
    }

    // Create a copy with all existing properties
    const result = { ...advice };

    // Initialize arrays if they don't exist
    result.urls = result.urls || [];
    result.page_source = result.page_source || [];
    result.page_title = result.page_title || [];
    result.urls_ccq = result.urls_ccq || [];
    result.page_source_ccq = result.page_source_ccq || [];
    result.page_title_ccq = result.page_title_ccq || [];
    result.texts_ccq = result.texts_ccq || [];
    result.caselaw_reference = result.caselaw_reference || [];
    result.caselaw_titre = result.caselaw_titre || [];
    result.caselaw_lienURL = result.caselaw_lienURL || [];
    result.caselaw_nomsDesPartis = result.caselaw_nomsDesPartis || [];

    // If relevant_forms doesn't exist, create it
    if (!result.relevant_forms) {
      result.relevant_forms = {
        page_url: [],
        doc_url: [],
        filename: [],
        title: [],
        content: [],
        filled_by: [],
      };
    }

    // Process each source based on its type
    advice.sources.forEach((source) => {
      switch (source.type) {
        case 'INFO':
          if (source.url && !result.urls?.includes(source.url)) {
            result.urls?.push(source.url);
            result.page_source?.push(source.pageSource || '');
            result.page_title?.push(source.pageTitle || '');
          }
          break;
        case 'LAW':
          if (source.url && !result.urls_ccq?.includes(source.url)) {
            result.urls_ccq?.push(source.url);
            result.page_source_ccq?.push(source.pageSource || '');
            result.page_title_ccq?.push(source.pageTitle || '');
            result.texts_ccq?.push(source.content || '');
          }
          break;
        case 'CASELAW':
          if (
            source.reference &&
            !result.caselaw_reference?.includes(source.reference)
          ) {
            result.caselaw_reference?.push(source.reference);
            result.caselaw_titre?.push(source.title || '');
            result.caselaw_lienURL?.push(source.url || '');
            result.caselaw_nomsDesPartis?.push(source.nomsDesPartis || '');
          }
          break;
        case 'FORM':
          // Only add to forms if not already present
          const existingFormIndex = result.relevant_forms?.doc_url?.indexOf(
            source.docUrl || ''
          );
          if (existingFormIndex === -1) {
            result.relevant_forms?.page_url?.push(source.pageUrl || '');
            result.relevant_forms?.doc_url?.push(source.docUrl || '');
            result.relevant_forms?.filename?.push(source.filename || '');
            result.relevant_forms?.title?.push(source.title || '');
            result.relevant_forms?.content?.push(source.content || '');
            result.relevant_forms?.filled_by?.push(source.filledBy || []);
          }
          break;
      }
    });

    return result;
  }, [advice]);

  // Log the processed advice for debugging
  // useEffect(() => {
  //   if (Array.isArray(advice?.sources) && advice.sources.length > 0) {
  //     console.log('[AIResponse] Processed advice from sources:', {
  //       adviceId: advice.id,
  //       sourceCount: advice.sources.length,
  //       hasUrls:
  //         Array.isArray(processedAdvice.urls) &&
  //         processedAdvice.urls.length > 0,
  //       hasLaws:
  //         Array.isArray(processedAdvice.urls_ccq) &&
  //         processedAdvice.urls_ccq.length > 0,
  //       hasCaselaw:
  //         Array.isArray(processedAdvice.caselaw_reference) &&
  //         processedAdvice.caselaw_reference.length > 0,
  //       hasForms:
  //         !!processedAdvice.relevant_forms &&
  //         (Array.isArray(processedAdvice.relevant_forms.doc_url) ||
  //           Array.isArray(processedAdvice.relevant_forms.page_url)),
  //     });
  //   }
  // }, [processedAdvice, advice?.id, advice?.sources]);

  return (
    <div
      className='relative w-full rounded-lg border border-border bg-card px-2 py-3 shadow-sm dark:border-gray-700/50 dark:bg-gray-800/50 dark:shadow-gray-900/30 sm:px-3 sm:py-4'
      data-has-content={hasAdviceContent ? 'true' : 'false'}
      data-has-sources={hasSourceContent ? 'true' : 'false'}
      data-is-streaming={isStreaming ? 'true' : 'false'}
      data-is-error={isError ? 'true' : 'false'}
    >
      <div key='content' className='w-full space-y-3 sm:space-y-4 md:space-y-5'>
        <div className='w-full text-xs dark:text-gray-100 sm:text-sm md:text-base'>
          {/* If we have no content but are in a loading/streaming state, show loading indicators */}
          {(!responseText || responseText.trim().length < 5) && !isError ? (
            <div className='flex min-h-[50px] items-center justify-center'>
              {isStreaming || isGenerating || isChatLoading ? (
                <div className='w-full'>
                  <MessageSkeleton />
                </div>
              ) : (
                <div className='animate-pulse text-muted-foreground'>
                  <div className='mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary/50 border-t-transparent' />
                  {tc('generating')}
                </div>
              )}
            </div>
          ) : (
            <Suspense fallback={<div>{tc('loading')}</div>}>
              <TextGenerateEffect
                words={responseText}
                className='prose prose-sm w-full max-w-none text-pretty break-words'
                loading={isGenerating || isChatLoading}
                adviceStatus={advice.status}
                isLastAdvice={isLastAdvice}
                shouldAnimate={
                  !isStreaming &&
                  !!advice.isNew &&
                  !wasStreamedRef.current &&
                  !isError
                }
                isNew={!!advice.isNew && !wasStreamedRef.current}
                isStreaming={isStreaming}
                showPlaceholder={!isGenerating && !isChatLoading && !isError}
                key={`${advice.id}${isError ? '-error' : ''}`}
                contentId={advice.id}
              />
            </Suspense>
          )}
        </div>

        {/* Show sources when they're available, even during streaming */}
        {hasSourceContent && !isError && !isStreaming && (
          <div className='mt-3 w-full pt-1 sm:mt-4 md:mt-5'>
            <SourcesInChatResponse advice={processedAdvice} />
          </div>
        )}

        {/* Feedback section */}
        <div className='flex items-center justify-between border-t border-gray-200 pt-3 dark:border-gray-700'>
          {/* Quick feedback buttons with counts */}
          <div className='flex items-center space-x-2'>
            <button
              onClick={() => handleQuickFeedback('positive')}
              disabled={isSubmitting}
              className={`flex items-center space-x-1 rounded-md p-1.5 transition-colors ${
                feedbackStatus === 'positive'
                  ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                  : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/50'
              }`}
              aria-label={t('helpful')}
            >
              <ThumbsUp className='h-5 w-5' />
            </button>
            <button
              onClick={() => handleQuickFeedback('negative')}
              disabled={isSubmitting}
              className={`flex items-center space-x-1 rounded-md p-1.5 transition-colors ${
                feedbackStatus === 'negative'
                  ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                  : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/50'
              }`}
              aria-label={t('notHelpful')}
            >
              <ThumbsDown className='h-5 w-5' />
            </button>
          </div>

          {/* Detailed feedback button or status */}
          {advice.feedback ? (
            <div className='flex items-center text-sm text-gray-500 dark:text-gray-400'>
              {/* {advice.feedback.details.comments && (
                <span className='mr-2 flex items-center'>
                  <MessageSquare className='h-4 w-4' />
                </span>
              )} */}
              <button
                onClick={handleDetailedFeedback}
                className='flex items-center space-x-1 rounded-md px-2 py-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700/50'
                aria-label='Edit feedback'
              >
                <Edit2 className='h-4 w-4' />
                <span className='hidden sm:inline'>
                  {tf('editFeedbackTitle')}
                </span>
              </button>
            </div>
          ) : (
            <button
              onClick={handleDetailedFeedback}
              className='flex items-center space-x-1 rounded-md px-2 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/50'
              aria-label='Give detailed feedback'
            >
              <MessageSquare className='h-4 w-4' />
              <span className='hidden sm:inline'>
                {tf('giveFeedbackTitle')}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        open={isFeedbackVisible}
        onOpenChange={setIsFeedbackVisible}
        adviceId={advice.id}
        initialReaction={feedbackStatus || 'positive'}
        existingFeedback={
          advice.feedback
            ? {
                reaction: advice.feedback.status,
                comments: advice.feedback.details.comments,
                createdAt: advice.feedback.details.createdAt,
                updatedAt: advice.feedback.details.updatedAt,
              }
            : null
        }
        question={advice.question || ''}
        response={advice.advice || ''}
      />
    </div>
  );
});

AIResponse.displayName = 'AIResponse';
